import React, { useContext, useEffect, useRef } from 'react';
import './WorkSection.css';
import { useSpring, animated, useTrail } from '@react-spring/web';
import { useInView } from 'react-intersection-observer';
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ScrollContext } from './ScrollContext'; // ✅ IMPORT SCROLL CONTEXT

// Import the images
import SZAMainImage from './CardContent/SZAPortal/SZAMAin.png';
import Image1 from './CardContent/SZAPortal/1.png';
import Image2 from './CardContent/SZAPortal/2.png';
import Image3 from './CardContent/SZAPortal/3.png';
import Image4 from './CardContent/SZAPortal/4.png';
import Image5 from './CardContent/SZAPortal/5.png';
import Image6 from './CardContent/SZAPortal/6.png';

import Health from './CardContent/SZAPortal/Health.png';
import Omics from './CardContent/SZAPortal/Omics.png';
import AI from './CardContent/SZAPortal/AI.png';

import NewGenome from './CardContent/SZAPortal/NewGenome.png';
import OncoRisk from './CardContent/SZAPortal/OncoRisk.png';
import NewGenome1 from './CardContent/SZAPortal/NewGenome1.png';
import GeneRisk from './CardContent/SZAPortal/GeneRisk.png';
import GVEPMain from './CardContent/SZAPortal/gvep_screen.png';
import GVEPM from './CardContent/SZAPortal/gvep_m.png';


import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faDotCircle, faGripHorizontal, faGripLines, faGripVertical, faArrowRight, faChevronLeft, faChevronRight, faCircle, faInfoCircle } from '@fortawesome/free-solid-svg-icons';

// A new, intelligent button component that handles both internal and external links.
const CtaButton = ({ to, className, children }) => {
  const isExternal = to && (to.startsWith('http') || to.startsWith('https'));

  if (isExternal) {
    return (
      <a href={to} className={className} target="_blank" rel="noopener noreferrer">
        {children}
      </a>
    );
  }

  return (
    <Link to={to} className={className}>
      {children}
    </Link>
  );
};

const WorkItem = ({ id, number, category, title, description, content, index, aboutLink, ctaLink }) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // ✅ Get the register function from context
  const { registerSection } = useContext(ScrollContext);
  // ✅ Create a ref for this component's main div
  const itemRef = useRef(null);

  // ✅ Register the component's element when it mounts
  useEffect(() => {
    if (itemRef.current && registerSection) {
      registerSection(id, itemRef.current);
    }
  }, [id, registerSection]); // Rerun if id or the function changes

  const itemSpring = useSpring({
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0px)' : 'translateY(60px)',
    config: { tension: 280, friction: 60 },
    delay: index * 200,
  });

  return (
    <animated.div ref={(node) => { ref(node); itemRef.current = node; }} style={itemSpring} className="ULT-work-item">
      <div className="ULT-work-item-header">
        <div className="ULT-work-number-section">
          <div className="ULT-work-number">{number}</div>
          <div className="ULT-work-category">{category}</div>
          
        </div>
        
        <div className="ULT-work-main-content">
          {title}
        </div>
        
        <div className="ULT-work-description">
          {description}
        </div>
        
        <div className="ULT-work-about-holder">
        <div className="ULT-work-about-button">
          <Link to={aboutLink} className="ULT-about-btn">
          <FontAwesomeIcon style={{ marginRight: '0.5vh', opacity: '0.8' }} icon={faInfoCircle} /> Learn More 








          </Link>
        </div>

        <div className="ULT-work-about-button">
          <CtaButton to={ctaLink} className="ULT-about-btn-plat">
            Try {category} Now <FontAwesomeIcon style={{ marginLeft: '0.5vh' }} icon={faArrowRight} />
          </CtaButton>
        </div>
        </div>

      </div>
      
      {/* Custom Content Section for each work item */}
      <div className="ULT-work-item-content">
        {content}
      </div>
    </animated.div>
  );
};

// Function to generate dot pattern
const generateDotPattern = (cols, rows) => {
  const dotRows = [];
  for (let i = 0; i < rows; i++) {
    const dots = [];
    for (let j = 0; j < cols; j++) {
      dots.push(<span key={j} className="ULT-dot"></span>);
    }
    dotRows.push(
      <div key={i} className="ULT-dot-row">
        {dots}
      </div>
    );
  }
  return dotRows;
};

const PlatformGrid = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const cards = [
    {
      id: 'neural-core',
      className: 'ULT-grid-card ULT-card-2x2 ULT-simple-card',
      image: SZAMainImage,
      title: 'Project Management',
      description: "Comprehensive project management with intelligent automation of infrastructure workflows and real-time decision support."    },
    {
      id: 'platform-suite',
      className: 'ULT-grid-card ULT-card-1x2 ULT-platform-suite-card',
      isPlatformSuite: true
    },
    {
      id: 'design-engine',
      className: 'ULT-grid-card ULT-card-1x1 ULT-simple-card',
      image: Image1,
      title: 'Patient Observation',
      description: "Real-time patient tracking and observation tools designed to streamline monitoring, data collection, and clinical insights."
    },
    {
      id: 'neural-net',
      className: 'ULT-grid-card ULT-card-1x1 ULT-simple-card',
      image: Image2,
      title: 'Patient and Cohort Managment',
      description: "Tools for managing individual patients and cohorts with integrated invitations, scheduling, and automated data organization."    },
    {
      id: 'shield-core',
      className: 'ULT-grid-card ULT-card-1x1 ULT-simple-card',
      image: Image3,
      title: 'In-House Sample Handling',
      description: "Secure and efficient in-house sample intake, processing, and tracking designed to ensure full control and traceability."
    },
    {
      id: 'data-flow',
      className: 'ULT-grid-card ULT-card-1x1 ULT-simple-card',
      image: Image4,
  title: 'Centralized Report Generation and Multilingual Delivery',
  description: 'Automated reporting pipeline with centralized control, supporting multilingual output and seamless distribution.'
    },

    {
      id: 'monitor',
      className: 'ULT-grid-card ULT-card-1x1 ULT-simple-card',
      image: Image6,
      title: 'Cloud Pipeline Processing',
      description: 'Automated pipelines that manage data processing, result sorting, and patient communication through a seamless cloud system.'
    },
    {
      id: 'api-gateway',
      className: 'ULT-grid-card ULT-card-1x1 ULT-simple-card',
      image: Image5,
      title: 'AI Omics Atlas',
      description: 'A multi-agent system that generates specialized agents for in-house multi-omics analysis, identifies novel discoveries in real time using clinical data, and visualizes the reasoning behind each result through live pipeline execution.'
    },
  ];

  const trail = useTrail(cards.length, {
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0px) scale(1)' : 'translateY(40px) scale(0.95)',
    config: { tension: 280, friction: 60 },
    delay: 300,
  });

  return (
    <div ref={ref} className="ULT-platform-content">
      <div className="ULT-platform-grid">
        {trail.map((style, index) => {
          const card = cards[index];
          return (
            <animated.div key={card.id} style={style} className={card.className}>
              {card.isPlatformSuite ? (
                // Platform Suite Card
                <>
              <div className="ULT-card-background"></div>
<div className="ULT-card-content ULT-platform-suite-content">
  <div className="ULT-product-lineup">Platform Overview </div>
  
  <h2 className="ULT-platform-title">
    End-to-End Research<br/>
    Management and Sample<br/>
    Processing Infrastructure
  </h2>
  
  <p className="ULT-platform-description">
    A streamlined suite for managing projects, tracking samples, and delivering reports—enhanced by intelligent AI support for Omics Discoveries.
  </p>
  
  <div className="ULT-dot-pattern">
    {generateDotPattern(12, 13)}
  </div>
</div>

                </>
              ) : (
                // Simple Cards
                <>
                  <div className="ULT-card-background" style={{ 
                    backgroundImage: `url(${card.image})`,
                    backgroundSize: 'cover',
                    backgroundPosition: card.id === 'neural-core' ? 'left' : 'center',
                    backgroundRepeat: 'no-repeat'
                  }}></div>
                  <div className="ULT-card-icon"><FontAwesomeIcon icon={faGripHorizontal} /></div>
                  <div className="ULT-card-content">
                    <h3>{card.title}</h3>
                  </div>
                  <div className="ULT-card-description">
                    {card.description}
                  </div>
                  <div className="ULT-card-button">
                    <FontAwesomeIcon icon={faArrowRight} />
                  </div>
                </>
              )}
            </animated.div>
          );
        })}
      </div>
    </div>
  );
};

// New Carousel Section Component
const CarouselSection = () => {
  const [currentPage, setCurrentPage] = useState(0);
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  // Sample carousel data - you can modify this with your actual content
  const pages = [

    
    {
      id: 0,
      leftContent: {
        image: Omics,
      },
      stepTitle: "Omics Insights and Reports",
      stepDescription: "View detailed multi-omics data processed through intelligent pipelines, with personalized PDF reports delivered in seven languages for global accessibility and clinical clarity.",
      bottomTitle: "Multi-Language Report Delivery",
      bottomSubtitle: "7-language support, personalized data"
    }
    ,
    {
      id: 1,
      leftContent: {
        image: Health,
      },
      stepTitle: "Seamless Wearable Tracking",
      stepDescription: "Track health metrics in real time with full support for Apple and Android wearables. Capture nutrition by snapping a photo, logging manually, or speaking directly to your AI assistant—all integrated into a continuous, personalized health feedback loop.",
      bottomTitle: "Compatible with 150+ Wearables",
      bottomSubtitle: "Realtime data, passive syncing"
    },
    {
      id: 2,
      leftContent: {
        image: AI,
      },
      stepTitle: "Longevity AI Integration",
      stepDescription: "Interact with your AI assistant through voice or text to explore your omics results, receive real-time feedback, and understand how your wearable data, nutrition, and lifestyle all connect in a personalized health narrative.",
      bottomTitle: "Conversational Health Intelligence",
      bottomSubtitle: "AI-driven feedback and insights"
    },
    {
      id: 3,
      leftContent: {
        image: Health,
      },
      stepTitle: "Cognitive Engagement",
      stepDescription: "Access a suite of scientifically designed cognitive games that adapt to your performance, helping monitor mental sharpness and support brain health as part of your daily routine.",
      bottomTitle: "Built-In Brain Training",
      bottomSubtitle: "Adaptive, science-backed games"
    }
    ,

    
  ];

  const handlePrevious = () => {
    setCurrentPage((prev) => (prev === 0 ? pages.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setCurrentPage((prev) => (prev === pages.length - 1 ? 0 : prev + 1));
  };

  const handleDotClick = (index) => {
    setCurrentPage(index);
  };

  const currentPageData = pages[currentPage];

  // Animation for content transitions
  const contentSpring = useSpring({
    opacity: 1,
    transform: 'translateX(0px)',
    from: { opacity: 0, transform: 'translateX(20px)' },
    reset: true,
    config: { tension: 280, friction: 60 }
  });

  const containerSpring = useSpring({
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0px)' : 'translateY(60px)',
    config: { tension: 280, friction: 60 },
    delay: 200,
  });

  return (
    <animated.div ref={ref} style={containerSpring} className="ULT-carousel-section">
      <div className="ULT-carousel-grid">
        {/* Left side - 4x2 */}
               <animated.div style={contentSpring} className="ULT-carousel-left ULT-grid-4x2">
          <img 
            src={currentPageData.leftContent.image} 
            alt={currentPageData.stepTitle} 
            className="ULT-carousel-left-image" 
          />
          <div className="ULT-carousel-left-content">
            <h2>{currentPageData.leftContent.title}</h2>
            <h3>{currentPageData.leftContent.subtitle}</h3>
            <p>{currentPageData.leftContent.description}</p>
          </div>
        </animated.div>
        {/* Top middle - 1x4 with title and progress dots */}
        <div className="ULT-carousel-top-middle ULT-grid-1x4">
          <div className="ULT-carousel-title">
            <h3>Longevity App</h3>
          </div>
          <div className="ULT-progress-dots">
            {pages.map((_, index) => (
              <button
                key={index}
                className={`ULT-progress-dot ${index === currentPage ? 'active' : ''}`}
                onClick={() => handleDotClick(index)}
              >
                <FontAwesomeIcon icon={index === currentPage ? faCircle : faDotCircle} />
              </button>
            ))}
          </div>
        </div>

        {/* Top right - 1x1 with navigation buttons */}
        <div className="ULT-carousel-nav ULT-grid-1x1">
          <button className="ULT-nav-btn ULT-nav-prev" onClick={handlePrevious}>
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <button className="ULT-nav-btn ULT-nav-next" onClick={handleNext}>
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>

        {/* Bottom - 2x5 with step content */}
        <animated.div style={contentSpring} className="ULT-carousel-bottom ULT-grid-2x5">
          <div className="ULT-carousel-step-content">
            <div className="ULT-step-header">
              <h2>{currentPageData.stepTitle}</h2>
              <p className="ULT-step-description">{currentPageData.stepDescription}</p>
            </div>
            <div className="ULT-step-footer">
              <h4 className="ULT-bottom-title">{currentPageData.bottomTitle}</h4>
              <p className="ULT-bottom-subtitle">{currentPageData.bottomSubtitle}</p>
            </div>
          </div>
        </animated.div>
      </div>
    </animated.div>
  );
};

// REFACTORED: New unified Glassmorphism Section Component
const GlassSection = ({
  alignment,
  mainTitle,
  subtitle,
  description,
  image,
  tileIcon,
  tileTitle,
  tileDescription
}) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const containerSpring = useSpring({
    opacity: inView ? 1 : 0,
    transform: inView ? 'translateY(0px)' : 'translateY(40px)',
    config: { tension: 280, friction: 60 },
    delay: 200,
  });

  // Reusable block for text content
  const TextContent = (
    <div className="ULT-glass-text-content">
      <div className="ULT-glass-layer ULT-glass-layer-1">
        <h2 className="ULT-glass-title">{mainTitle}</h2>
      </div>
      <div className="ULT-glass-layer ULT-glass-layer-2">
        <h3 className="ULT-glass-subtitle">{subtitle}</h3>
      </div>
      <div className="ULT-glass-layer ULT-glass-layer-3">
        <p className="ULT-glass-description">{description}</p>
      </div>
    </div>
  );

  // Reusable block for the image tile
  const ImageTile = (
    <div className="ULT-glass-content-tile">
      <div className="ULT-content-tile-background" style={{
        backgroundImage: `url(${image})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}></div>
      <div className="ULT-content-tile-overlay">
        <div className="ULT-tile-content">
          <div className="ULT-tile-icon">
            <FontAwesomeIcon icon={tileIcon} />
          </div>
          <h4>{tileTitle}</h4>
          <p>{tileDescription}</p>
        </div>
      </div>
    </div>
  );

  return (
    <animated.div ref={ref} style={containerSpring} className="ULT-glass-section">
      <div className={`ULT-glass-container ULT-content-${alignment}`}>
        {/* Conditionally render order based on alignment prop */}
        {alignment === 'right' ? (
          <>
            {TextContent}
            {ImageTile}
          </>
        ) : (
          <>
            {ImageTile}
            {TextContent}
          </>
        )}
      </div>
    </animated.div>
  );
};


const WorkSection = () => {
  const [titleRef, titleInView] = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });

  const titleSpring = useSpring({
    opacity: titleInView ? 1 : 0,
    transform: titleInView ? 'translateY(0px)' : 'translateY(30px)',
    config: { tension: 280, friction: 60 },
  });

  const workItems = [
    {
      id: "xomics", // ✅ ADDED

      number: "001",
      category: "XOmics Portal",
      title: "A comprehensive platform for end-to-end project and infrastructure management, sample processing and tracking, automated report delivery, and integration of multi-agent AI systems.",
      description: "Designed for seamless in-house integrations of all services. From data handling, analysis and omics discovery to intelligent report delivery.",
      content: <PlatformGrid />,
      aboutLink: "/xomics/about",
      ctaLink: "/dashboard"
    },
    {
      id: "genriskpro", // ✅ ADDED

      number: "002",
      category: "GenRiskPro",
      title: "Versatile Platform for Genomic Analysis and Report Generation",
      description: "Streamlined solution for processing genomic data, conducting in-depth analyses, and producing structured, high-quality reports with ease.",
      content: <GlassSection 
        alignment="right"
        image={GeneRisk}
        mainTitle="Genomics Reporting Platform"
        subtitle="Variant Processing, Analysis, and Delivery"
        description="A unified system for navigating large-scale genomic datasets, performing query-driven analysis, and generating detailed, structured reports efficiently."
        tileIcon={faGripVertical}
        tileTitle="Real-time Insights"
        tileDescription="On-demand variant interpretation and data response"
      />,
      aboutLink: "/genriskpro/about",
      ctaLink: "/genriskpro"

    },
    
    {
      id: "familyrisk",

      number: "003",
      category: "FamilyRisk",
      title: "Family-Based Variant Analysis for Newborn Screening",
      description: "Process VCF and PED files with individual, family-based, and cohort-level analysis modes for identifying recessive, de novo, and compound heterozygous variants.",
      content: <GlassSection
        alignment="left"
        image={NewGenome}
        mainTitle="FamilyRisk"
        subtitle="Newborn & Family Variant Analysis"
        description="Identify recessive, de novo, and compound heterozygous variants with pedigree visualization. Compare results across NBSeq projects like BabySeq, BeginNGS, and GUARDIAN."
        tileIcon={faGripLines}
        tileTitle="Multi-Mode Analysis"
        tileDescription="Individual, family, cohort & comparative modes"
      />,
      aboutLink: "/familyrisk/about",
      ctaLink: "https://xinmengliao.github.io/PhenomePortal-FamilyRisk/Home/"
    },
    
    {
      id: "oncorisk", // ✅ ADDED

      number: "004",
      category: "OncoRisk",
      title: "Comprehensive Oncology Platform for Data Exploration, Analysis, and Reporting",
      description: "Access one of the largest oncology databases combined with advanced research tools, AI-assisted exploration, and automated report generation—all in a unified system.",
      content: <GlassSection 
        alignment="right"
        image={OncoRisk}
        mainTitle="Comprehensive Oncology Platform"
        subtitle="Data Exploration, Analysis, and Reporting"
        description="Access one of the largest oncology databases combined with advanced research tools, AI-assisted exploration, and automated report generation—all in a unified system."
        tileIcon={faGripVertical}
        tileTitle="Real-time Processing"
        tileDescription="Instant data analysis and response"
      />,
      aboutLink: "/oncorisk/about",
      ctaLink: "/oncorisk"
    },
    {
      id: "varxomics", // ✅ ADDED

      number: "005",
      category: "VarXOmics",
      title: "VCF & Query-Based Genomics Platform for Exploration and Reporting",
      description: "Explore and visualize genomic data from large databases with query-driven workflows and generate detailed reports—all in one system.",
      content: <GlassSection 
        alignment="left"
        image={NewGenome1}
        mainTitle="Comprehensive Genomics Platform"
        subtitle="Exploration, Visualization, and Reporting"
        description="Explore and visualize genomic data from large databases with query-driven workflows and generate detailed reports—all in one system."
        tileIcon={faGripLines}
        tileTitle="Genomic Data Exploration"
        tileDescription="VCF & query-based workflows"
      />,
      aboutLink: "/varxomics/about",
      ctaLink: "/varxomics"
    },
    {
      id: "gvep",
      number: "006",
      category: "G-VEP",
      title: "GPU-Accelerated Variant Effect Predictor for Same-Day Genome Interpretation",
      description: "17-fold faster annotation with CUDA-powered parallel binary search. Reduces whole-genome annotation from 100 minutes to 33 minutes while maintaining complete concordance.",
      content: <GlassSection
        alignment="right"
        image={GVEPMain}
        mainTitle="G-VEP"
        subtitle="GPU-Accelerated Variant Annotation"
        description="CUDA-powered parallel annotation replaces sequential database lookups. Fits in consumer-grade 16GB GPUs with 8.8GB index footprint. Available as CLI, web server, and REST API."
        tileIcon={faGripVertical}
        tileTitle="17x Faster"
        tileDescription="Plugin runtime: 72min → 4min"
      />,
      aboutLink: "/gvep/about",
      ctaLink: "/gvep"
    },
    {
      id: "polygen",
      number: "007",
      category: "PolyGen",
      title: "Polygenic Risk Score Computation for Complex Trait Prediction",
      description: "Multi-method PRS platform integrating GWAS catalog data with population-calibrated percentile rankings for accurate genetic risk assessment.",
      content: <GlassSection
        alignment="left"
        image={GVEPMain}
        mainTitle="PolyGen"
        subtitle="Polygenic Risk Score Platform"
        description="Compute polygenic risk scores using PRS-CS, LDpred2, and SBayesR methods. Population-calibrated percentiles with multi-ancestry support and PGS Catalog integration."
        tileIcon={faGripVertical}
        tileTitle="Multi-PRS"
        tileDescription="PRS-CS, LDpred2, SBayesR"
      />,
      aboutLink: "/polygen",
      ctaLink: "/polygen"
    },
    {
      id: "mobile",

      number: "008",
      category: "Mobile Application",
      title: "Patient-facing app for omics insights, clinical reporting, and AI interaction.",
      description: "Explore your omics results, receive clinical insights, and communicate directly with your AI—all in one streamlined interface.",
      content: <CarouselSection />,
      aboutLink: "/mobileapp/about",
      ctaLink: "https://apps.apple.com/se/app/sza-longevity/id6468774325"
    },
  ];

  return (
    <section className="ULTHOME-work-section">
      <animated.h2 ref={titleRef} style={titleSpring} className="ULT-ourwork">Our Work</animated.h2>
      <div className="ULT-work-items-container">
        {workItems.map((item, index) => (
          <WorkItem 
            key={index}
            id={item.id} // ✅ PASS THE ID
            index={index}
            number={item.number}
            category={item.category}
            title={item.title}
            description={item.description}
            content={item.content}
            aboutLink={item.aboutLink}
            ctaLink={item.ctaLink}
          />
        ))}
      </div>
    </section>
  );
};

export default WorkSection;